import { AnthropicKeySection } from '../managed-agents/anthropic-key-section';
import type { WorkspaceKeyStatus } from '../../../lib/managed-agents/types';
import { CARD_CLASS } from './constants';
import { StatusPill } from './status-pill';

interface ApiKeysTabProps {
  workspaceId: string;
  isAdmin: boolean;
  keyStatus: WorkspaceKeyStatus | null;
  onKeyChange: (status: WorkspaceKeyStatus) => void;
}

/** One key per provider, shared by the workspace. */
export function ApiKeysTab({
  workspaceId,
  isAdmin,
  keyStatus,
  onKeyChange,
}: ApiKeysTabProps) {
  return (
    <div className="flex flex-col gap-4">
      <p className="max-w-3xl text-sm text-prompt-safecomabbey">
        Keys are shared by your workspace. Only admins can change them.
      </p>

      <div className={`${CARD_CLASS} flex flex-col gap-4 p-5`}>
        <ProviderHeader
          mark="A"
          markClassName="bg-[#1F1F1F] text-white"
          name="Anthropic"
          status={
            keyStatus?.connected ? (
              <StatusPill tone="ok">Connected</StatusPill>
            ) : (
              <StatusPill>Not connected</StatusPill>
            )
          }
        />
        <div className="flex flex-wrap gap-1.5">
          <UsedFor>Claude Console agents</UsedFor>
        </div>
        {keyStatus && (
          <AnthropicKeySection
            workspaceId={workspaceId}
            isAdmin={isAdmin}
            status={keyStatus}
            onChange={onKeyChange}
          />
        )}
      </div>

      <div className={`${CARD_CLASS} flex flex-col gap-3 p-5`}>
        <ProviderHeader
          mark="O"
          name="OpenAI"
          status={<StatusPill dot={false}>Included in your plan</StatusPill>}
        />
        <p className="text-sm text-prompt-safecomabbey">
          GPT models run on PromptSafe's key and use your PS tokens. Nothing to
          set up.
        </p>
      </div>
    </div>
  );
}

function ProviderHeader({
  mark,
  markClassName = 'border border-[#00000020] bg-prompt-safecomwhite text-prompt-safecomvulcan',
  name,
  status,
}: {
  mark: string;
  markClassName?: string;
  name: string;
  status: React.ReactNode;
}) {
  return (
    <div className="flex items-center gap-3">
      <span
        aria-hidden="true"
        className={`grid h-9 w-9 place-items-center rounded-lg font-semibold ${markClassName}`}
      >
        {mark}
      </span>
      <h3 className="font-semibold text-prompt-safecomvulcan">{name}</h3>
      <span className="ml-auto">{status}</span>
    </div>
  );
}

function UsedFor({ children }: { children: React.ReactNode }) {
  return (
    <span className="rounded-md border border-[#00000014] bg-prompt-safecomwhite px-2 py-0.5 text-xs text-prompt-safecomvulcan">
      Used for: {children}
    </span>
  );
}
